import { motion, useScroll, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ArrowDown, HandHeart } from "lucide-react";
import { API, LOGO_URL, scrollToId, formatRupiah, formatNumber } from "@/lib/site";
import { EASE, Tilt, CountUp } from "./motion-prims";

const LINES = [
  { text: "BANTU", cls: "text-ink" },
  { text: "DONASI", cls: "italic text-gold" },
  { text: "BERSAMA PR", cls: "text-olive" },
];

export default function Hero() {
  const { data: campaigns } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => (await axios.get(`${API}/campaigns`)).data,
  });

  const totalCollected = (campaigns || []).reduce((a, c) => a + c.collected, 0);
  const totalDonors = (campaigns || []).reduce((a, c) => a + c.donors, 0);
  const activeCount = (campaigns || []).length;

  const { scrollY } = useScroll();
  const badgeY = useTransform(scrollY, [0, 600], [0, 90]);
  const glowY = useTransform(scrollY, [0, 600], [0, -60]);

  return (
    <section id="beranda" className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
      <div className="halftone absolute inset-x-0 top-0 h-[420px] opacity-40 pointer-events-none" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-8 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05, ease: EASE }}
            className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-gold-dark"
          >
            Public Relations 2025 ✦ Gerakan Sosial
          </motion.p>

          <h1 className="mt-5 font-display font-black leading-[0.95] tracking-tight text-[clamp(3.4rem,10vw,7.6rem)]">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-pine"
          >
            Satu langkah kecil, ribuan senyum. Pilih program kerja, salurkan donasi terbaik, dan pantau
            dampaknya bersama-sama.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-program"
              onClick={() => scrollToId("program")}
              className="btn-retro bg-olive text-cream rounded-full px-7 py-3.5 text-sm sm:text-base"
            >
              Pilih Program
            </button>
            <button
              data-testid="hero-cta-quick"
              onClick={() => scrollToId("donasi")}
              className="btn-retro bg-cream text-ink rounded-full px-7 py-3.5 text-sm sm:text-base"
            >
              <HandHeart className="w-4 h-4" /> Donasi Cepat
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
            className="mt-12 grid grid-cols-3 max-w-xl divide-x-2 divide-ink/10"
          >
            <div className="pr-3" data-testid="hero-stat-collected">
              <CountUp value={totalCollected} format={formatRupiah} className="block font-display font-black text-[13px] sm:text-2xl text-olive" />
              <p className="mt-1 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-pine">Terkumpul</p>
            </div>
            <div className="px-3">
              <CountUp value={totalDonors} className="block font-display font-black text-lg sm:text-2xl text-olive" />
              <p className="mt-1 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-pine">Donatur</p>
            </div>
            <div className="pl-3">
              <CountUp value={activeCount} className="block font-display font-black text-lg sm:text-2xl text-olive" />
              <p className="mt-1 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-pine">Program Aktif</p>
            </div>
          </motion.div>
        </div>

        <div className="relative flex justify-center lg:justify-end">
          <motion.div style={{ y: glowY }} className="absolute inset-0 grid place-items-center pointer-events-none" aria-hidden="true">
            <div className="sunburst w-[130%] aspect-square rounded-full" />
          </motion.div>
          <motion.div style={{ y: badgeY }} className="relative">
            <Tilt max={12} className="animate-float">
              <div
                data-testid="hero-badge"
                className="w-[240px] h-[240px] sm:w-[320px] sm:h-[320px] lg:w-[400px] lg:h-[400px] rounded-full overflow-hidden border-4 border-ink shadow-retro-lg"
              >
                <img src={LOGO_URL} alt="Badge Public Relations 2025" className="w-full h-full object-cover" />
              </div>
            </Tilt>
            <motion.div
              initial={{ opacity: 0, scale: 0.6, rotate: -18 }}
              animate={{ opacity: 1, scale: 1, rotate: -8 }}
              transition={{ duration: 0.7, delay: 1.05, ease: EASE }}
              className="absolute -bottom-4 -left-4 sm:-left-10 bg-gold border-2 border-ink rounded-full px-5 py-3 shadow-retro"
            >
              <p className="font-display font-black text-sm sm:text-base leading-tight">
                100% Disalurkan
                <span className="block text-[10px] font-body font-bold uppercase tracking-[0.2em]">untuk program</span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("program")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="absolute bottom-2 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-1 text-pine"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.3em]">Gulir</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown className="w-4 h-4" />
        </motion.span>
      </motion.button>
    </section>
  );
}
